import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, Key, ReactElement, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { customScrollStyles } from '@gem/ui-kit';

import styles from './List.styles.m.css';
import ListItem from './ListItem';

interface IListProps<T = any> {
  readonly className?: string;
  readonly data: T[];
  readonly renderItem: (props: T) => ReactElement<T>;
  readonly keyExtractor: (item: T, index: number) => Key;
  readonly ListEmptyComponent?: ReactElement;
  readonly SpinnerComponent?: ReactElement;
  readonly NotInitializedComponent?: ReactElement;
  readonly isLoading?: boolean;
  readonly willChange?: boolean;
  readonly isScrollable?: boolean;
  readonly isInitializedContent?: boolean;
  readonly hasMore?: boolean;
  readonly onEndReached?: () => void;
}

export const List: FC<IListProps> = observer((props) => {
  const {
    className,
    data,
    renderItem,
    keyExtractor,
    ListEmptyComponent,
    SpinnerComponent,
    NotInitializedComponent,
    isLoading = false,
    willChange = false,
    isScrollable = false,
    isInitializedContent = true,
    hasMore = false,
    onEndReached,
  } = props;

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && onEndReached && hasMore && !isLoading) {
      onEndReached();
    }
  }, [inView, onEndReached, hasMore, isLoading]);

  if (!isInitializedContent && NotInitializedComponent) {
    return NotInitializedComponent;
  }

  if (!isLoading && !data.length && !hasMore) {
    return ListEmptyComponent || null;
  }

  const classes = classNames(styles.container, className, {
    [styles.willChange]: willChange,
    [styles.scrollable]: isScrollable,
    [customScrollStyles.container]: isScrollable,
  });

  return (
    <div className={classes}>
      {data.map((item, index) => (
        <ListItem
          key={keyExtractor(item, index)}
          renderItem={renderItem}
          item={item}
        />
      ))}
      {onEndReached && hasMore && !isLoading && (
        <div
          className={styles.showMore}
          ref={ref}
        />
      )}
      {(isLoading && SpinnerComponent) || null}
    </div>
  );
});
